/**
 * Single relative-scale authority for the isometric world.
 *
 * PROBLEM this solves: on-screen height used to be set independently in three
 * places — sprite PNGs drew at their baked pixel size (`scale: 1`), procedural
 * props used hardcoded `h` values, and the avatar drew at `u = 2`. Nothing tied
 * them together, so a ~60px avatar could out-top a ~50px street-lamp sprite.
 *
 * Here the **person is the yardstick** (`PERSON_PX`). Every other kind declares a
 * target height as a multiple of a person (`KIND_HEIGHT`), and both render paths
 * derive their pixels from it:
 *   - sprite path:      scale = KIND_HEIGHT[kind] * PERSON_PX / contentHeightPx
 *   - procedural path:  h     = KIND_HEIGHT[kind] * PERSON_PX
 *
 * KINDS NOT LISTED in `KIND_HEIGHT` are intentionally left at scale 1 (inert) so
 * seat-paired indoor furniture (desk/chair/couch/sofa1/stool/bench/pod) — which is
 * art-directed to line up with seated avatars — is not disturbed. Only the tall,
 * freestanding props where proportions were actually wrong opt in.
 */

import type { MapFurnitureKind } from "@/lib/office/personal-office-layout";

/**
 * Avatar silhouette height at zoom 1, in world px. The standing avatar spans
 * ~30 local units × `u = 2` (see drawAvatar in world-engine.js; matches the
 * `-32*u` head-label clearance in `overlayHeadOffset`). This is the reference —
 * the avatar itself is unchanged; everything else is sized against it.
 */
export const PERSON_PX = 60;

/**
 * Target visual height per kind, expressed in **person-heights** (1.0 == PERSON_PX).
 * Real-world intuition: person ≈ 1.75 m, so a value of 2.6 ≈ a 4.5 m street lamp.
 *
 * Only kinds that needed correcting are listed. Anything omitted resolves to
 * scale 1 via {@link spriteScaleFor} / is left alone in the procedural switch.
 */
export const KIND_HEIGHT: Partial<Record<MapFurnitureKind, number>> = {
  // Tall outdoor / street — must clearly tower over a person.
  streetlamp: 2.6,
  tree: 3.0,
  pine: 3.2,
  cafe_umbrella: 1.4,
  plaza_sign: 1.5,
  fountain: 1.15,
  delivery_truck: 2.2, // vehicle scale (also gets a bigger footprint in catalog.ts)
  // Low outdoor props — should sit well below a person.
  // Bench reads as a real park bench (seat + backrest ~ chest-high on a person),
  // not a tiny speck next to the trees/HQ.
  bench: 0.8,
  bollard: 0.55,
  fence: 0.7,
  rock: 0.5,
  flower: 0.45,
  planter: 0.6,
  flowerbed: 0.6,
  // Foliage shrubs — around waist/chest height, never person-tall.
  bush: 0.95,
  hedge: 1.0,
  // ── Indoor FREESTANDING props (person-anchored so PNG bake size can't drift) ──
  // Deliberately excludes SEAT-PAIRED furniture (desk/chair/couch/sofa1/stool/pod/
  // cushion/beanbag): those are art-directed to line up with seated avatars and must
  // stay at scale 1. Only pieces nobody sits *on* opt in here.
  bookshelf: 1.15, // ~2.0 m shelving — clearly taller than a person's shoulders
  arcade: 1.1,
  jukebox: 0.95,
  cooler: 0.8, // water cooler ~ chest-high
  lamp: 1.1, // indoor floor lamp
  neon: 0.8, // wall sign
  fox: 0.5, // desk-top mascot statue
  reception: 0.85, // welcome desk with a raised hood
  coffeetable: 0.32, // low lounge table ~ knee height
  plant: 0.9, // big office planter ~ chest/shoulder height
};

/** Clamp so a mis-measured/degenerate sprite can never explode or vanish. */
function clampScale(s: number): number {
  if (!Number.isFinite(s) || s <= 0) return 1;
  return Math.max(0.2, Math.min(4, s));
}

/**
 * Draw scale for a sprite of `kind` whose non-transparent content is
 * `contentHeightPx` tall (measure with {@link measureContentHeight}). Kinds not in
 * {@link KIND_HEIGHT} return 1 (draw at natural size — no behaviour change).
 */
export function spriteScaleFor(kind: string, contentHeightPx: number): number {
  const target = KIND_HEIGHT[kind as MapFurnitureKind];
  if (target === undefined) return 1;
  if (!(contentHeightPx > 0)) return 1;
  return clampScale((target * PERSON_PX) / contentHeightPx);
}

/**
 * Target pixel height for a procedural (no-PNG) prop of `kind`, or `null` when the
 * kind opts out (leave its hand-tuned box heights alone). Drop-in for the `h`
 * argument to `box()` for single-dominant-height props; multi-segment props scale
 * their segments proportionally toward this total.
 */
export function proceduralHeightPx(kind: string): number | null {
  const target = KIND_HEIGHT[kind as MapFurnitureKind];
  return target === undefined ? null : target * PERSON_PX;
}

/**
 * Measure the height (in px) of the non-transparent content of a loaded sprite,
 * bottom-aligned art inside a mostly-transparent canvas. Memoized per image element.
 * Browser-only (needs a 2D canvas); falls back to the image's natural height when
 * pixels can't be read (SSR, tainted canvas, decode-in-flight).
 */
const contentHeightCache = new WeakMap<CanvasImageSource & object, number>();

export function measureContentHeight(
  img: HTMLImageElement | HTMLCanvasElement | ImageBitmap,
): number {
  const cached = contentHeightCache.get(img);
  if (cached !== undefined) return cached;

  const w = "naturalWidth" in img ? img.naturalWidth : img.width;
  const h = "naturalHeight" in img ? img.naturalHeight : img.height;
  const natural = h || 1;

  if (typeof document === "undefined" || !w || !h) return natural;

  try {
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return natural;
    ctx.drawImage(img, 0, 0);
    const data = ctx.getImageData(0, 0, w, h).data;
    let minY = h;
    let maxY = -1;
    for (let y = 0; y < h; y++) {
      let rowHit = false;
      const rowStart = y * w * 4;
      for (let x = 0; x < w; x++) {
        if ((data[rowStart + x * 4 + 3] ?? 0) >= 32) {
          rowHit = true;
          break;
        }
      }
      if (rowHit) {
        if (y < minY) minY = y;
        maxY = y;
      }
    }
    const measured = maxY >= minY ? maxY - minY + 1 : natural;
    contentHeightCache.set(img, measured);
    return measured;
  } catch {
    return natural;
  }
}
